import { ArchiveModalAPI } from '@/components/ConfirmationModal';
import { TErrorNotification } from '@/components/ErrorInlineNotification/ErrorInlineNotification';
import { useDrawerClose } from '@/context/Drawer';
import {
  useReceivedInvoiceArchive,
  useReceivedInvoiceRestoreArchive,
} from '@/context/services/invoice/ReceivedInvoice.service';
import { useTranslation } from '@/i18n';
import { API } from '@moneyforward/ap-frontend-components';
import { useCallback } from 'react';

type APIResult = 'success' | 'error';

export const useCallArchiveCallback = (modal: ArchiveModalAPI, notify: API) => {
  const { t } = useTranslation();
  const drawerClose = useDrawerClose();
  return useCallback(
    async (result: APIResult) => {
      modal.close();
      if (result === 'success') {
        drawerClose();
        notify.success({
          title: t('invoice_archive_message_success'),
          opts: {
            duration: 3000,
            style: {
              position: 'fixed',
            },
          },
          bodyProps: {
            disabledSuffix: true,
          },
        });
      }
    },
    [drawerClose, modal, notify, t]
  );
};

export const useCallArchive = (
  id: string,
  callback: (result: APIResult) => void,
  setError: TErrorNotification['setError']
) => {
  const { mutateAsync } = useReceivedInvoiceArchive(id);
  return useCallback(async () => {
    let result: APIResult = 'success';
    try {
      await mutateAsync({ id: id });
    } catch (err) {
      result = 'error';
      // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
      setError(err as Error);
    } finally {
      callback(result);
    }
  }, [callback, id, mutateAsync, setError]);
};

export const useCallRestoreArchiveCallback = (notify: API) => {
  const { t } = useTranslation();
  return useCallback(
    (result: APIResult) => {
      if (result === 'success') {
        notify.success({
          title: t('invoice_restore_message_success'),
          opts: {
            duration: 3000,
            style: {
              position: 'fixed',
            },
          },
          bodyProps: {
            disabledSuffix: true,
          },
        });
      }
    },
    [notify, t]
  );
};

export const useCallRestoreArchive = (
  id: string,
  callback: (result: APIResult) => void,
  setError: TErrorNotification['setError']
) => {
  const { mutateAsync } = useReceivedInvoiceRestoreArchive(id);
  return useCallback(async () => {
    let result: APIResult = 'success';
    try {
      await mutateAsync(
        { id },
        {
          refetchQueries: ['ReceivedInvoice', 'ReceivedInvoices'],
        }
      );
    } catch (err) {
      result = 'error';
      // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
      setError(err as Error);
    } finally {
      callback(result);
    }
  }, [callback, id, mutateAsync, setError]);
};
